export type PortalErrorKind =
  | 'invalidPublicKey'
  | 'publicKeyNotAuthorized'
  | 'authenticationFailed'
  | 'applicationAccessDenied'
  | 'signOutIncomplete'
  | 'sessionExpired'
  | 'forbidden'
  | 'notFound'
  | 'rateLimited'
  | 'invalidRequest'
  | 'network'
  | 'invalidResponse'
  | 'server'
  | 'unexpected';

const CUSTOMER_MESSAGES: Record<PortalErrorKind, string> = {
  invalidPublicKey:
    'The public key file is invalid. Upload a PEM-encoded RSA-4096 public key and try again.',
  publicKeyNotAuthorized:
    'This public key does not have access to this channel. Contact AWS Secret Region support to confirm that the exact public key you uploaded is approved, then try again.',
  authenticationFailed:
    'Authentication failed. Try again, and contact AWS Secret Region support if the problem continues.',
  applicationAccessDenied:
    'Your account does not have access to this application. Contact AWS Secret Region support to request access.',
  signOutIncomplete:
    'Sign out may be incomplete. Close your browser to ensure your session is ended.',
  sessionExpired: 'Your session has expired. Sign in again.',
  forbidden: 'You do not have permission to perform this action.',
  notFound: 'The requested resource was not found.',
  rateLimited: 'Too many requests were submitted. Wait a moment and try again.',
  invalidRequest: 'The request could not be completed. Try again.',
  network: 'The Portal could not reach the service. Check your connection and try again.',
  invalidResponse: 'The service returned an invalid response. Try again later.',
  server: 'A server error occurred. Try again later.',
  unexpected: 'An unexpected error occurred. Try again later.',
};

// Customer-safe application error; diagnostic details stay in internal logs.
export class PortalError extends Error {
  constructor(
    public readonly kind: PortalErrorKind,
    public readonly retryable = false,
  ) {
    super(CUSTOMER_MESSAGES[kind]);
    this.name = 'PortalError';
  }
}

// Customer-safe HTTP error with diagnostic fields for control flow and logging.
export class ServiceError extends PortalError {
  constructor(
    kind: PortalErrorKind,
    public readonly statusCode: number,
    public readonly errorCode = '',
  ) {
    super(kind, kind === 'server');
    this.name = 'ServiceError';
  }

  isRetryable(): boolean {
    return this.retryable;
  }
}

export function classifyServiceError(statusCode: number, errorCode: string): PortalErrorKind {
  switch (errorCode) {
    case 'INVALID_PUBLIC_KEY':
      return 'invalidPublicKey';
    case 'PUBLIC_KEY_NOT_AUTHORIZED':
      return 'publicKeyNotAuthorized';
    case 'SESSION_EXPIRED':
    case 'AUTHENTICATION_FAILED':
      return 'sessionExpired';
    case 'FORBIDDEN':
      return 'forbidden';
    case 'NOT_FOUND':
      return 'notFound';
    case 'RATE_LIMIT_EXCEEDED':
      return 'rateLimited';
    case 'INTERNAL_ERROR':
      return 'server';
    default:
      break;
  }

  if (statusCode === 401) return 'sessionExpired';
  if (statusCode === 403) return 'forbidden';
  if (statusCode === 404) return 'notFound';
  if (statusCode === 429) return 'rateLimited';
  if (statusCode >= 500) return 'server';
  return 'invalidRequest';
}

export function toPortalError(error: unknown, fallback: PortalErrorKind = 'unexpected'): PortalError {
  return error instanceof PortalError ? error : new PortalError(fallback);
}

export function toCustomerMessage(error: unknown): string {
  return error instanceof PortalError ? CUSTOMER_MESSAGES[error.kind] : CUSTOMER_MESSAGES.unexpected;
}

export function isRetryableError(error: unknown): boolean {
  return error instanceof PortalError && error.retryable;
}

export function customerMessageFor(kind: PortalErrorKind): string {
  return CUSTOMER_MESSAGES[kind];
}
